import html2canvas from 'html2canvas'
import { useCallback, useRef } from 'react'
import { useChannel } from 'src/provider/ChannelContextProvider'
import { useModal } from 'src/provider/ModalContextProvider'
import { useToast } from 'src/provider/ToastContextProvider'
import { mobileOS } from 'src/utils'

import { Bridge } from 'src/utils/bridge'

export type LayoutClipboardShareResult =
  | 'shared'
  | 'share_fail'
  | 'copy'
  | 'copy_fail'
  | 'no_ref'
  | 'aborted'
  | 'fail'

const useHtml2CanvasDown = () => {
  const componentRef = useRef<HTMLDivElement>(null)
  const { openToast } = useToast()
  const { openModal } = useModal()
  const { web } = useChannel()
  const os = mobileOS()

  const handleDownloadClick = (callbackFn: () => void) => {
    if (componentRef.current) {
      html2canvas(componentRef.current, { useCORS: true, logging: true }).then((canvas) => {
        const url = canvas?.toDataURL()

        Bridge.handleReqSaveImage(url, () => {
          const link = document.createElement('a')
          link.href = url
          link.download = 'couponwallet.png'

          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        })
      })
    }
    callbackFn()
  }

  const handleShareClick = useCallback(async (): Promise<LayoutClipboardShareResult> => {
    const el = componentRef.current
    if (!el) return 'no_ref'

    let canvas: HTMLCanvasElement
    try {
      canvas = await html2canvas(el, { useCORS: true, logging: false })
    } catch {
      return 'fail'
    }

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/png')
    })
    if (!blob) return 'fail'

    const file = new File([blob], 'couponwallet.png', { type: 'image/png' })
    const sharePayload = {
      files: [file] as [File],
      title: '휴대폰쿠폰지갑',
    }

    const showUnsupportedModal = () => {
      openModal({
        contents: { d: '지원되지 않는 브라우저 환경입니다.' },
        button: { submit: { t: '확인' } },
      })
    }

    if (web && (os === 'android' || os === 'ios')) {
      //web + android|ios: OS 공유 시도
      if (typeof navigator.share === 'function' && navigator.canShare?.(sharePayload)) {
        try {
          await navigator.share(sharePayload)
          return 'shared'
        } catch (e) {
          if ((e as Error).name === 'AbortError') return 'aborted'
        }
      }
      showUnsupportedModal()
      return 'share_fail'
    } else {
      if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
        try {
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
          openToast({ contents: '이미지가 클립보드에 복사되었습니다.' })
          return 'copy'
        } catch {
          showUnsupportedModal()
          return 'copy_fail'
        }
      }
      showUnsupportedModal()
      return 'fail'
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    componentRef,
    handleDownloadClick,
    handleShareClick,
  }
}
export default useHtml2CanvasDown
